import React, { useEffect, useMemo, useRef, useState } from "react";
import GridLayout from "react-grid-layout";
import { Boxes } from "lucide-react";
import { minSizeFor } from "@/lib/layoutEngine";
import { cn } from "@/lib/utils";

const CHILD_HANDLES = ["s", "w", "e", "n", "sw", "nw", "se", "ne"];
const DROP_RING = "ring-2 ring-inset ring-[color:var(--brand-ocean)] bg-[color:rgba(43,78,255,0.06)] rounded-xl";

// Layout modes supported by the Combine Widget (container).
export const CONTAINER_LAYOUTS = [
  { value: "free", label: "Free (pixel placement)" },
  { value: "grid", label: "Grid (auto compact)" },
  { value: "vertical", label: "Vertical stack" },
  { value: "horizontal", label: "Horizontal row" },
  { value: "responsive", label: "Responsive (auto wrap)" },
];

// Item 27: Tab Widget — an in-canvas tab set built on the container engine. Each inner tab keeps
// its own free-form/grid layout, only the open tab is mounted (lazy), and the open tab is a deep link.
export const TAB_POSITIONS = [["top", "Top"], ["bottom", "Bottom"], ["left", "Left"], ["right", "Right"]];
export const TAB_STYLES = [["underline", "Underline"], ["pill", "Pills"], ["segment", "Segmented"]];

export const containerTabs = (widget) => {
  const tabs = ((widget.config || {}).container || {}).tabs;
  return Array.isArray(tabs) ? tabs.filter((t) => t && t.id) : [];
};
export const childTabId = (child) => (child.layout_child?.tab || child.config?.container_tab || "");
const urlTabKey = (wid) => `ct_${String(wid).slice(0, 12)}`;

function TabStrip({ tabs, activeId, onSelect, pos, styleKind, widgetId }) {
  const vertical = pos === "left" || pos === "right";
  const base = "text-[11px] px-2.5 py-1 transition-colors whitespace-nowrap";
  const cls = (on) => {
    if (styleKind === "pill") return cn(base, "rounded-full", on ? "bg-[color:var(--brand-ocean)] text-white" : "text-[color:var(--ink-3)] hover:text-[color:var(--ink)] hover:bg-[color:var(--surface-2)]");
    if (styleKind === "segment") return cn(base, "rounded-md", on ? "bg-[color:var(--surface)] text-[color:var(--ink)] shadow-sm" : "text-[color:var(--ink-3)] hover:text-[color:var(--ink)]");
    return cn(base, "border-b-2 -mb-px", on ? "border-[color:var(--brand-ocean)] text-[color:var(--brand-ocean)] font-medium" : "border-transparent text-[color:var(--ink-3)] hover:text-[color:var(--ink)]");
  };
  return (
    <div data-testid={`tabwidget-strip-${widgetId}`}
      className={cn("flex gap-1 shrink-0 overflow-auto",
        vertical ? "flex-col py-1 pr-1 border-r border-[color:var(--glass-border)] max-w-[150px]" : "flex-row items-center px-1",
        !vertical && styleKind === "underline" && "border-b border-[color:var(--glass-border)]",
        styleKind === "segment" && "bg-[color:var(--surface-2)] rounded-lg p-0.5")}>
      {tabs.map((t) => (
        <button key={t.id} data-testid={`tabwidget-tab-${t.id}`} data-nodrag
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => { e.stopPropagation(); onSelect(t.id); }}
          className={cls(t.id === activeId)} title={t.name}>
          {t.name}
        </button>
      ))}
    </div>
  );
}

function sortKids(kids) {
  return [...kids].sort((a, b) =>
    ((a.layout_child?.order ?? a.layout_child?.y ?? 0) - (b.layout_child?.order ?? b.layout_child?.y ?? 0))
    || ((a.layout_child?.x ?? 0) - (b.layout_child?.x ?? 0)));
}

// Body of a container widget: renders its child widgets in one of 5 layouts.
// Shared by Builder (editing) and Viewer (static).
export function ContainerBody(props) {
  const tabs = containerTabs(props.widget);
  if (tabs.length) return <TabbedContainerBody {...props} tabs={tabs} />;
  return <ContainerLayoutBody {...props} />;
}

// Item 27: tabbed wrapper — remembers the open tab in the URL, mounts only the open tab.
function TabbedContainerBody({ tabs, ...props }) {
  const { widget, childWidgets, activeTabId, onTabSelect } = props;
  const cc = (widget.config || {}).container || {};
  const pos = cc.tab_pos || "top";
  const key = urlTabKey(widget.id);
  const fromUrl = () => {
    try {
      const v = new URLSearchParams(window.location.search).get(key);
      return v && tabs.some((t) => t.id === v) ? v : "";
    } catch { return ""; }
  };
  const [local, setLocal] = useState(() => fromUrl() || tabs[0].id);
  const active = tabs.some((t) => t.id === activeTabId) ? activeTabId : (tabs.some((t) => t.id === local) ? local : tabs[0].id);
  const select = (tid) => {
    setLocal(tid);
    onTabSelect && onTabSelect(tid);
    try {
      const u = new URL(window.location.href);
      u.searchParams.set(key, tid);
      window.history.replaceState({}, "", u.pathname + u.search);
    } catch { /* non-browser */ }
  };
  const inTab = (childWidgets || []).filter((k) => {
    const t = childTabId(k);
    return t ? t === active : active === tabs[0].id;
  });
  const vertical = pos === "left" || pos === "right";
  const strip = <TabStrip tabs={tabs} activeId={active} onSelect={select} pos={pos} styleKind={cc.tab_style || "underline"} widgetId={widget.id} />;
  return (
    <div data-testid={`tabwidget-${widget.id}`} className={cn("h-full w-full min-h-0 flex", vertical ? (pos === "right" ? "flex-row-reverse" : "flex-row") : (pos === "bottom" ? "flex-col-reverse" : "flex-col"))}>
      {strip}
      <div className="flex-1 min-h-0 min-w-0">
        <ContainerLayoutBody {...props} childWidgets={inTab} activeTab={active} />
      </div>
    </div>
  );
}

function ContainerLayoutBody({ widget, childWidgets, renderChild, editing = false, onChildLayout, onChildDragStop = null, depth = 1, onAdoptSelection, selectedCount = 0, dropActive = false, activeTab = null }) {
  const cc = (widget.config || {}).container || {};
  const mode = cc.layout || "free";
  const gap = Number(cc.gap ?? 8);
  const pad = Number(cc.padding ?? 8);
  const hostRef = useRef(null);
  const [width, setWidth] = useState(600);
  useEffect(() => {
    const el = hostRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setWidth(el.clientWidth));
    ro.observe(el);
    setWidth(el.clientWidth);
    return () => ro.disconnect();
  }, []);

  const kids = useMemo(() => sortKids(childWidgets || []), [childWidgets]);

  if (!kids.length) {
    return (
      <div data-testid="container-empty" data-container-id={widget.id} className={cn("h-full w-full flex items-center justify-center transition-colors", dropActive && DROP_RING)}>
        <div className="text-center text-[color:var(--ink-3)] text-sm px-4">
          <Boxes className="w-7 h-7 mx-auto mb-2 opacity-60" />
          <div className="font-medium text-[color:var(--ink-2)]">{dropActive ? "Release to add the widget here" : "Drag and drop one or more widgets here"}</div>
          <div className="text-xs mt-1">Drag widgets from the library or the canvas onto this container, or select widgets and press <b>Combine</b>.</div>
          {editing && onAdoptSelection && selectedCount > 0 && (
            <button data-testid="container-adopt-btn" onClick={(e) => { e.stopPropagation(); onAdoptSelection(); }}
              className="mt-3 text-xs px-3 py-1.5 rounded-lg bg-[color:var(--brand-ocean)] text-white hover:opacity-90">
              Move {selectedCount} selected widget{selectedCount > 1 ? "s" : ""} here
            </button>
          )}
        </div>
      </div>
    );
  }

  if (mode === "vertical" || mode === "horizontal") {
    return (
      <div ref={hostRef} data-testid={`container-body-${mode}`} data-container-id={widget.id}
        className={cn("h-full w-full flex overflow-auto transition-colors", mode === "vertical" ? "flex-col" : "flex-row", dropActive && DROP_RING)}
        style={{ gap, padding: pad }}>
        {kids.map((k) => (
          <div key={k.id} id={`wcap-${k.id}`} className="min-w-0 min-h-0"
            style={{ flex: `${Math.max(1, k.layout_child?.size || 1)} 1 0%`, minHeight: mode === "vertical" ? 120 : undefined, minWidth: mode === "horizontal" ? 160 : undefined }}>
            {renderChild(k)}
          </div>
        ))}
      </div>
    );
  }

  if (mode === "responsive") {
    return (
      <div ref={hostRef} data-testid="container-body-responsive" data-container-id={widget.id} className={cn("h-full w-full overflow-auto transition-colors", dropActive && DROP_RING)}
        style={{
          display: "grid", gap, padding: pad,
          gridTemplateColumns: `repeat(auto-fit, minmax(${Number(cc.min_w || 240)}px, 1fr))`,
          gridAutoRows: `${Number(cc.row_h || 200)}px`,
        }}>
        {kids.map((k) => <div key={k.id} id={`wcap-${k.id}`} className="min-w-0 min-h-0">{renderChild(k)}</div>)}
      </div>
    );
  }

  // free / grid: nested grid layout with per-child x/y/w/h
  const overlap = mode === "free" && !!cc.overlap;
  const layout = kids.map((k, i) => {
    const l = k.layout_child || {};
    const min = minSizeFor(k.type);
    return { i: k.id, x: l.x ?? 0, y: l.y ?? i * 4, w: Math.max(min.w, l.w ?? 6), h: Math.max(min.h, l.h ?? 4), minW: min.w, minH: min.h, static: !!k.locked };
  });
  return (
    <div ref={hostRef} data-testid={`container-body-${mode}`} data-container-id={widget.id} className={cn("h-full w-full overflow-auto transition-colors", dropActive && DROP_RING)}>
      <GridLayout className="layout" layout={layout} width={Math.max(220, width)} cols={12}
        rowHeight={Number(cc.row_h || 40)} margin={[gap, gap]} containerPadding={[pad, pad]}
        compactType={mode === "grid" ? "vertical" : null} preventCollision={mode === "free" && !overlap} allowOverlap={overlap}
        isDraggable={editing} isResizable={editing} resizeHandles={CHILD_HANDLES}
        draggableHandle={`.child-drag-${depth}`} draggableCancel="button, a, input, textarea, select, [role='menuitem'], [data-nodrag], .react-resizable-handle"
        onDragStop={editing && onChildDragStop ? (lay, oldItem, newItem, ph, e, el) => onChildDragStop(widget.id, newItem?.i, e, el) : undefined}
        onLayoutChange={(lay) => {
          if (!editing || !onChildLayout) return;
          const map = {};
          lay.forEach((l) => { map[l.i] = { x: l.x, y: l.y, w: l.w, h: l.h }; });
          const changed = kids.some((k) => {
            const l = map[k.id]; const c = k.layout_child || {};
            return l && (l.x !== c.x || l.y !== c.y || l.w !== c.w || l.h !== c.h);
          });
          if (changed) onChildLayout(map);
        }}>
        {kids.map((k) => <div key={k.id} id={`wcap-${k.id}`}>{renderChild(k)}</div>)}
      </GridLayout>
    </div>
  );
}
